/**
 * Read a maneuver baseline's stored fit.
 *
 * The server fits every numeric maneuver channel per config x event type against TWS and stores the
 * curves sampled onto one shared grid (server_app/utils/maneuverBaselineFit.js). Nothing is fitted
 * here -- these are readers, so the number a table prints and the point a chart draws come from the
 * same stored curve.
 *
 * The maneuver scatter's x axis is always `tws_avg`, so a curve is a direct plot of
 * (tws_grid, channel.values) -- no parametric walk like the straight-line `baselineCurve.ts` needs.
 */

import type {
  ManeuverBaselineFitJson,
  ManeuverBaselineFitBucket,
  ManeuverBaselineMember,
  ManeuverEventTypeKey,
} from '../services/baselinesService';
import { maneuverChannelAliasKeys } from './maneuverChannelAliases';

export const MANEUVER_EVENT_TYPE_KEYS: ManeuverEventTypeKey[] =
  ['TACK', 'GYBE', 'ROUNDUP', 'BEARAWAY', 'TAKEOFF'];

/** One row of the flattened baseline series: the TWS grid point plus every channel's value there. */
export interface ManeuverBaselineSeriesRow {
  tws_avg: number;
  [channel: string]: number | null;
}

function normalizeEventType(eventType: string | null | undefined): ManeuverEventTypeKey | null {
  const key = String(eventType || '').trim().toUpperCase() as ManeuverEventTypeKey;
  return MANEUVER_EVENT_TYPE_KEYS.includes(key) ? key : null;
}

/** Config keys a maneuver fit holds, sorted. */
export function maneuverBaselineConfigs(fit: ManeuverBaselineFitJson | null | undefined): string[] {
  if (!fit?.configs) { return []; }
  return Object.keys(fit.configs).sort();
}

/**
 * Config keys that actually hold a bucket for one maneuver type, sorted.
 *
 * The picker must offer these rather than every config in the fit: a config fitted only from gybes
 * is a real config, but choosing it while the page shows tacks draws nothing at all, with no
 * explanation. An unrecognised event type falls back to every config, so a page whose type this
 * module does not know still lists something.
 */
export function maneuverBaselineConfigsForType(
  fit: ManeuverBaselineFitJson | null | undefined,
  eventType: string | null | undefined
): string[] {
  const type = normalizeEventType(eventType);
  if (!fit?.configs || !type) { return maneuverBaselineConfigs(fit); }
  return Object.keys(fit.configs).filter((config) => !!fit.configs[config]?.[type]).sort();
}

/** The `{ n, members, channels }` block for one config + event type, or null when absent. */
export function maneuverBaselineBucket(
  fit: ManeuverBaselineFitJson | null | undefined,
  config: string,
  eventType: string | null | undefined
): ManeuverBaselineFitBucket | null {
  const type = normalizeEventType(eventType);
  if (!fit?.configs || !config || !type) { return null; }
  return fit.configs[config]?.[type] || null;
}

/** The member maneuvers behind one config + event type, for the kernel-weighted overlays. */
export function maneuverBaselineMembers(
  fit: ManeuverBaselineFitJson | null | undefined,
  config: string,
  eventType: string | null | undefined
): ManeuverBaselineMember[] {
  return maneuverBaselineBucket(fit, config, eventType)?.members || [];
}

/**
 * The keys a chart's channel name can be stored under, in priority order.
 *
 * Two ways a name can differ from a fit key: case (fit keys are lowercase, some chart channels are
 * mixed-case) and the display aliases the maneuver scatter uses (`rake_aoa_min` is stored as
 * `rake_aoa_min_old_turn`). Without both, those charts would silently draw no baseline.
 */
function channelLookupKeys(channel: string): string[] {
  const raw = String(channel || '');
  if (raw === '') { return []; }
  const lower = raw.toLowerCase();
  const keys = [raw, lower];
  for (const alias of maneuverChannelAliasKeys(lower) || []) {
    keys.push(alias, alias.toLowerCase());
  }
  return keys;
}

/** The stored curve for one channel of a bucket, resolving case and display aliases. */
export function maneuverBaselineChannel(
  bucket: ManeuverBaselineFitBucket | null,
  channel: string
): { values: Array<number | null>; method: string; n: number } | null {
  if (!bucket?.channels) { return null; }
  for (const key of channelLookupKeys(channel)) {
    if (bucket.channels[key]) { return bucket.channels[key]; }
  }
  return null;
}

/** One channel's value from a flattened series row, or null when the baseline does not cover it. */
export function readSeriesValue(
  row: ManeuverBaselineSeriesRow | null | undefined,
  channel: string
): number | null {
  if (!row) { return null; }
  for (const key of channelLookupKeys(channel)) {
    const v = row[key];
    if (typeof v === 'number' && Number.isFinite(v)) { return v; }
  }
  return null;
}

/**
 * The fit flattened to one row per grid point: `[{ tws_avg, mmg, loss_total_tgt, ... }]`.
 *
 * This is the row shape every maneuver reader already understands, so tables and charts can look up
 * a baseline value the same way they look up a maneuver's own. Grid points where no channel has a
 * value are dropped -- they carry nothing and would only widen a chart's domain.
 */
export function maneuverFitToSeries(
  fit: ManeuverBaselineFitJson | null | undefined,
  config: string,
  eventType: string | null | undefined
): ManeuverBaselineSeriesRow[] {
  const bucket = maneuverBaselineBucket(fit, config, eventType);
  const grid = fit?.tws_grid;
  if (!bucket || !Array.isArray(grid) || grid.length === 0) { return []; }

  const entries = Object.entries(bucket.channels || {});
  const out: ManeuverBaselineSeriesRow[] = [];
  for (let i = 0; i < grid.length; i++) {
    const row: ManeuverBaselineSeriesRow = { tws_avg: grid[i] };
    let any = false;
    for (const [key, curve] of entries) {
      const v = curve?.values?.[i];
      if (typeof v === 'number' && Number.isFinite(v)) {
        row[key] = v;
        any = true;
      }
    }
    if (any) { out.push(row); }
  }
  return out;
}

/**
 * The series row at one TWS, interpolated between the two neighbouring grid rows.
 *
 * Clamped at both ends rather than extrapolated, and a channel missing on either side yields null
 * for that channel only -- the stored fit already refuses to cover wind speeds its members did not
 * reach, and bridging that here would undo the guard.
 */
export function seriesAtTws(
  series: ManeuverBaselineSeriesRow[] | null | undefined,
  tws: number
): ManeuverBaselineSeriesRow | null {
  if (!series || series.length === 0) { return null; }
  const x = Number(tws);
  if (!Number.isFinite(x)) { return null; }
  if (series.length === 1) { return series[0]; }
  if (x <= series[0].tws_avg) { return series[0]; }
  if (x >= series[series.length - 1].tws_avg) { return series[series.length - 1]; }

  for (let i = 1; i < series.length; i++) {
    if (x <= series[i].tws_avg) {
      const a = series[i - 1];
      const b = series[i];
      const span = b.tws_avg - a.tws_avg;
      const f = span === 0 ? 0 : (x - a.tws_avg) / span;
      const row: ManeuverBaselineSeriesRow = { tws_avg: x };
      for (const key of Object.keys(a)) {
        if (key === 'tws_avg') { continue; }
        const av = a[key];
        const bv = b[key];
        row[key] = (typeof av === 'number' && typeof bv === 'number')
          ? av + f * (bv - av)
          : null;
      }
      return row;
    }
  }
  return series[series.length - 1];
}

/** One drawable point of a baseline curve. */
export interface ManeuverBaselineCurvePoint {
  tws: number;
  value: number;
}

/**
 * One channel's curve as segments of consecutive covered TWS points.
 *
 * Segments rather than one polyline because a gap means the baseline's members did not reach that
 * wind speed; joining across it would draw a reference through data nobody sailed. Single-point
 * segments are kept -- the scatter still marks them even where a line cannot be drawn.
 */
export function seriesCurveSegments(
  series: ManeuverBaselineSeriesRow[] | null | undefined,
  channel: string
): ManeuverBaselineCurvePoint[][] {
  if (!series || series.length === 0) { return []; }
  const segments: ManeuverBaselineCurvePoint[][] = [];
  let current: ManeuverBaselineCurvePoint[] = [];
  for (const row of series) {
    const v = readSeriesValue(row, channel);
    if (v != null && Number.isFinite(row.tws_avg)) {
      current.push({ tws: row.tws_avg, value: v });
    } else if (current.length > 0) {
      segments.push(current);
      current = [];
    }
  }
  if (current.length > 0) { segments.push(current); }
  return segments;
}

/** Members selected for a wind speed, with their kernel weights. */
export interface ManeuverKernelSelection {
  members: ManeuverBaselineMember[];
  weights: number[];
  /** The σ actually used, after any widening. */
  sigma: number;
}

/**
 * Default kernel width, kph.
 *
 * The maneuver reports bin TWS in steps of 5 kph and each bin owns exactly ±2.5 (the hard-coded band
 * in every page's `filteredData_tws`). The kernel has to be no wider than the step the user takes, or
 * two adjacent bins select nearly the same members at nearly the same weights and draw the same path.
 *
 * kph rather than the display unit on purpose: a member's `tws` is `Tws_avg` in kph, so a kts band
 * half of 1.0 would be a silently wrong width here.
 */
export const MANEUVER_KERNEL_SIGMA_KPH = 2.5;
/** Truncation, in σ. With σ = the band half-width this reaches one bin either side. */
const KERNEL_TRUNCATION_SIGMA = 2;
/** Widen until at least this many members qualify. */
const KERNEL_MIN_MEMBERS = 3;
/**
 * Hard ceiling on widening, as a multiple of the base σ. Without one a sparsely-covered baseline
 * widens until it swallows every member, which draws the whole baseline under one wind speed's label.
 * Averaging few maneuvers and reporting the effective σ is the lesser evil.
 */
const KERNEL_MAX_SIGMA_FACTOR = 3;

/**
 * Members near a wind speed, Gaussian-weighted by how close their own TWS is.
 *
 * The same local-regression idea as the stored channel fits, in the form the map and the timeseries
 * need: one weight per member, which the callers apply per time step to produce ONE path and ONE
 * trace. Sharing it between them is what guarantees the path and the trace describe the same
 * maneuvers.
 *
 * σ widens by half again whenever too few members are in range, so a thinly-covered wind speed gives
 * a broader average rather than nothing at all.
 */
export function kernelWeightsForTws(
  members: readonly ManeuverBaselineMember[],
  tws: number,
  options: { sigma?: number; minMembers?: number } = {}
): ManeuverKernelSelection {
  const withTws = members
    .map((member) => ({ member, x: Number(member?.tws) }))
    .filter((m) => Number.isFinite(m.x));
  if (withTws.length === 0 || !Number.isFinite(tws)) {
    return { members: [], weights: [], sigma: options.sigma ?? MANEUVER_KERNEL_SIGMA_KPH };
  }

  const minMembers = Math.max(1, options.minMembers ?? KERNEL_MIN_MEMBERS);
  const base = Math.max(1e-6, options.sigma ?? MANEUVER_KERNEL_SIGMA_KPH);
  const maxSigma = base * KERNEL_MAX_SIGMA_FACTOR;
  let sigma = base;

  for (;;) {
    const cutoff = KERNEL_TRUNCATION_SIGMA * sigma;
    const inRange = withTws.filter((m) => Math.abs(m.x - tws) <= cutoff);
    const enough = inRange.length >= Math.min(minMembers, withTws.length);
    const atCap = sigma >= maxSigma - 1e-9;

    if (enough || atCap) {
      // Nothing even at the widest kernel: fall back to the nearest few rather than to everything, so
      // a wind speed the baseline barely covers gives its closest maneuvers, not its whole range.
      const chosen = inRange.length > 0
        ? inRange
        : [...withTws]
          .sort((a, b) => Math.abs(a.x - tws) - Math.abs(b.x - tws))
          .slice(0, Math.min(minMembers, withTws.length));
      return {
        members: chosen.map((m) => m.member),
        weights: chosen.map((m) => {
          const z = (m.x - tws) / sigma;
          return Math.exp(-0.5 * z * z);
        }),
        sigma,
      };
    }
    sigma = Math.min(sigma * 1.5, maxSigma);
  }
}

export interface CurveBounds {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

/**
 * Y range of a baseline curve inside an x window.
 *
 * Points outside the window are ignored. A span that crosses either edge contributes the y at that
 * edge, so the visible part of the line is fully inside the range even when the samples sit outside.
 */
export function baselineCurveYExtent(
  segments: ManeuverBaselineCurvePoint[][],
  xMin: number,
  xMax: number
): { min: number; max: number } | null {
  if (!Number.isFinite(xMin) || !Number.isFinite(xMax)) { return null; }
  const lo = Math.min(xMin, xMax);
  const hi = Math.max(xMin, xMax);
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  const take = (value: number): void => {
    if (!Number.isFinite(value)) { return; }
    if (value < min) { min = value; }
    if (value > max) { max = value; }
  };
  const yAt = (a: ManeuverBaselineCurvePoint, b: ManeuverBaselineCurvePoint, x: number): number => {
    const span = b.tws - a.tws;
    if (span === 0) { return a.value; }
    return a.value + ((x - a.tws) / span) * (b.value - a.value);
  };

  for (const segment of segments) {
    for (let i = 0; i < segment.length; i++) {
      const point = segment[i];
      if (point.tws >= lo && point.tws <= hi) { take(point.value); }
      const next = segment[i + 1];
      if (!next) { continue; }
      if ((point.tws < lo && next.tws > lo) || (next.tws < lo && point.tws > lo)) { take(yAt(point, next, lo)); }
      if ((point.tws < hi && next.tws > hi) || (next.tws < hi && point.tws > hi)) { take(yAt(point, next, hi)); }
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) { return null; }
  return { min, max };
}

/**
 * Clip a segment to the chart's own domain, splitting it where it leaves and re-enters.
 *
 * The scatter widens its y domain to fit the baseline first. What remains outside — usually the
 * wind speeds the current runs do not cover — is cut at the edge (Liang-Barsky on each span, so a
 * segment crossing the boundary still meets it exactly rather than stopping short).
 */
export function clipCurveToBounds(
  points: ManeuverBaselineCurvePoint[],
  bounds: CurveBounds
): ManeuverBaselineCurvePoint[][] {
  const inside = (p: ManeuverBaselineCurvePoint) =>
    p.tws >= bounds.xMin && p.tws <= bounds.xMax
    && p.value >= bounds.yMin && p.value <= bounds.yMax;

  if (points.length === 0) { return []; }
  if (points.length === 1) { return inside(points[0]) ? [[points[0]]] : []; }

  /** Where the span a->b crosses into / out of the box, as parameters [t0, t1] or null. */
  const clipSpan = (
    a: ManeuverBaselineCurvePoint,
    b: ManeuverBaselineCurvePoint
  ): [number, number] | null => {
    const dx = b.tws - a.tws;
    const dy = b.value - a.value;
    let t0 = 0;
    let t1 = 1;
    const edges: Array<[number, number]> = [
      [-dx, a.tws - bounds.xMin],
      [dx, bounds.xMax - a.tws],
      [-dy, a.value - bounds.yMin],
      [dy, bounds.yMax - a.value],
    ];
    for (const [p, q] of edges) {
      if (p === 0) {
        if (q < 0) { return null; }   // parallel to this edge and outside it
        continue;
      }
      const r = q / p;
      if (p < 0) {
        if (r > t1) { return null; }
        if (r > t0) { t0 = r; }
      } else {
        if (r < t0) { return null; }
        if (r < t1) { t1 = r; }
      }
    }
    return [t0, t1];
  };

  const at = (
    a: ManeuverBaselineCurvePoint,
    b: ManeuverBaselineCurvePoint,
    t: number
  ): ManeuverBaselineCurvePoint => ({
    tws: a.tws + (b.tws - a.tws) * t,
    value: a.value + (b.value - a.value) * t,
  });

  const out: ManeuverBaselineCurvePoint[][] = [];
  let current: ManeuverBaselineCurvePoint[] = [];
  const pushCurrent = () => {
    if (current.length > 0) { out.push(current); current = []; }
  };

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const span = clipSpan(a, b);
    if (!span) { pushCurrent(); continue; }
    const [t0, t1] = span;
    const start = t0 === 0 ? a : at(a, b, t0);
    const end = t1 === 1 ? b : at(a, b, t1);
    if (current.length === 0) {
      current.push(start);
    } else {
      const prev = current[current.length - 1];
      // A span that starts where the last one ended continues the same visible run.
      if (Math.abs(prev.tws - start.tws) > 1e-9 || Math.abs(prev.value - start.value) > 1e-9) {
        pushCurrent();
        current.push(start);
      }
    }
    current.push(end);
    if (t1 < 1) { pushCurrent(); }   // left the box inside this span
  }
  pushCurrent();
  return out.filter((seg) => seg.length > 0);
}
